import { useCurrentRoute } from "@/hooks/useCurrentRoute";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ArrowDownCircle, ArrowUpCircle } from "lucide-react";

export interface RouteDisplayProps {
  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Show breadcrumb trail instead of just current page
   */
  showBreadcrumbs?: boolean;

  /**
   * Maximum number of breadcrumb items to show
   */
  maxBreadcrumbItems?: number;

  /**
   * Text size variant
   */
  size?: "sm" | "md" | "lg";

  /**
   * Whether to show the route icon
   */
  showIcon?: boolean;
}

export function RouteDisplay({
  className,
  showBreadcrumbs = false,
  maxBreadcrumbItems = 3,
  size = "md",
  showIcon = false
}: RouteDisplayProps) {
  const { displayName, breadcrumbs, current, isActive } = useCurrentRoute();

  const sizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg"
  };

  const getBreadcrumbDisplay = () => {
    if (breadcrumbs.length <= maxBreadcrumbItems) {
      return breadcrumbs.map((item, index) => (
        <div key={item.path} className="flex items-center">
          {index > 0 && (
            <span className="text-muted-foreground mx-2 text-xs">›</span>
          )}
          <span className={cn(
            "transition-colors",
            item.isLast
              ? "text-foreground font-medium"
              : "text-muted-foreground hover:text-foreground"
          )}>
            {item.title}
          </span>
        </div>
      ));
    }

    // Show truncated breadcrumbs with ellipsis for long paths
    const visibleItems = breadcrumbs.slice(0, maxBreadcrumbItems - 1);
    const lastItem = breadcrumbs[breadcrumbs.length - 1];

    return (
      <>
        {visibleItems.map((item, index) => (
          <div key={item.path} className="flex items-center">
            {index > 0 && (
              <span className="text-muted-foreground mx-2 text-xs">›</span>
            )}
            <span className="text-muted-foreground hover:text-foreground transition-colors">
              {item.title}
            </span>
          </div>
        ))}
        <span className="text-muted-foreground mx-2 text-xs">...</span>
        {lastItem && (
          <span className="text-foreground font-medium">
            {lastItem.title}
          </span>
        )}
      </>
    );
  };

  return (
    <div className={cn(
      "flex items-center justify-between",
      sizeClasses[size],
      className
    )}>
      <div className="flex items-center gap-3">
        {showIcon && current?.icon && (
          <div className="text-primary">
            <current.icon className="h-4 w-4" />
          </div>
        )}

        {showBreadcrumbs ? (
          <div className="flex items-center">
            {getBreadcrumbDisplay()}
          </div>
        ) : null}
      </div>

      {!showBreadcrumbs && (
        <div className="flex items-center gap-2">
          <Badge
            variant="default"
            className="h-9 w-56 justify-center overflow-hidden text-sm whitespace-nowrap text-white dark:text-white shadow-md rounded-sm bg-gradient-to-r from-sidebar-primary to-sidebar-primary/90 hover:from-sidebar-primary/95 hover:to-sidebar-primary/85 transition-all duration-200 dark:from-accent dark:to-accent/90 dark:hover:from-accent/95 dark:hover:to-accent/85"
            title={displayName}
          >
            {displayName}
          </Badge>
          {(() => {
            const path = current?.path;
            if (path === '/transactions/incomes') {
              return <ArrowUpCircle className="h-8 w-8 text-finance-green" />;
            }
            if (path === '/transactions/expenses') {
              return <ArrowDownCircle className="h-8 w-8 text-finance-red" />;
            }
            const Icon = current?.icon;
            return Icon ? <Icon className="h-6 w-6 text-primary" /> : null;
          })()}
        </div>
      )}
    </div>
  );
}
